import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Storefront, IdentificationBadge, MapPin, Phone, EnvelopeSimple, Tag, Globe, CheckCircle, Clock, PencilSimple } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { initials, fmtDate } from "@/lib/format";

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-3 border-b border-black/5 last:border-0">
    <Icon size={18} className="text-muted-foreground mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="font-medium break-words">{value || "—"}</p>
    </div>
  </div>
);

export default function BizProfile() {
  const [biz, setBiz] = useState(null);
  const [idn, setIdn] = useState(null);

  useEffect(() => {
    api.get("/business/me").then((r) => setBiz(r.data)).catch(() => {});
    api.get("/business/identity").then((r) => setIdn(r.data)).catch(() => {});
  }, []);

  const editName = async () => {
    const current = idn?.onekite_name || "";
    const val = window.prompt("Set your public OneKite Name (3-20 chars, letters/numbers/underscore):", current);
    if (val === null) return;
    try {
      const { data } = await api.post("/business/onekite-name", { name: val.replace(/^@/, "") });
      toast.success("OneKite Name updated");
      setIdn((p) => ({ ...p, onekite_name: data.onekite_name }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!biz) return <div className="text-muted-foreground">Loading…</div>;
  const active = biz.status === "active";

  return (
    <div className="space-y-6" data-testid="business-profile-page">
      <div>
        <h1 className="font-display font-bold text-3xl flex items-center gap-2"><Storefront size={26} className="text-primary" /> Business Profile</h1>
        <p className="text-muted-foreground text-sm">The details you provided when registering on OneKite.</p>
      </div>

      {/* Header with logo + status */}
      <div className="card-surface p-6 flex items-start gap-4 flex-wrap">
        <div className="h-16 w-16 rounded-2xl bg-primary/15 flex items-center justify-center font-display font-bold text-primary text-xl overflow-hidden shrink-0">
          {biz.logo ? <img src={biz.logo} alt="" className="h-full w-full object-cover" /> : initials(biz.name)}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-bold text-2xl">{biz.name}</h2>
          {idn?.onekite_name && <p className="text-primary text-sm">@{idn.onekite_name}</p>}
          <p className="text-xs text-muted-foreground mt-1">{biz.category} · {biz.city}{biz.country ? `, ${biz.country}` : ""}</p>
        </div>
        <span data-testid="profile-status" className={`text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 ${active ? "text-emerald-700 bg-emerald-600/10" : "text-amber-700 bg-amber-500/10"}`}>
          {active ? <CheckCircle size={14} weight="fill" /> : <Clock size={14} />} {active ? "Active" : (biz.status === "pending" ? "Pending approval" : biz.status)}
        </span>
      </div>

      {/* Top identity card — same as the main dashboard */}
      {idn && (
        <div className="card-surface p-5 flex flex-wrap items-center gap-x-10 gap-y-3" data-testid="business-identity">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-primary/15 flex items-center justify-center"><IdentificationBadge size={22} weight="duotone" className="text-primary" /></div>
            <div>
              <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">OneKite ID</p>
              <p className="font-display font-bold text-xl tabular-nums" data-testid="profile-onekite-id">{idn.onekite_id_formatted}</p>
            </div>
          </div>
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">OneKite Name</p>
            <div className="flex items-center gap-2">
              <p className="font-display font-bold text-lg" data-testid="profile-onekite-name">{idn.onekite_name ? `@${idn.onekite_name}` : "Not set"}</p>
              <button onClick={editName} data-testid="edit-onekite-name" className="text-muted-foreground hover:text-primary p-1"><PencilSimple size={16} /></button>
            </div>
          </div>
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Account type</p>
            <p className="font-medium">{idn.account_type}</p>
          </div>
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Branches</p>
            <p className="font-medium">{idn.branches.length}</p>
          </div>
        </div>
      )}

      {/* Registration details */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-surface p-5">
          <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-1">Business details</p>
          <Row icon={Storefront} label="Business name" value={biz.name} />
          <Row icon={Tag} label="Category" value={biz.category} />
          <Row icon={MapPin} label="Location" value={[biz.city, biz.country].filter(Boolean).join(", ")} />
          <Row icon={Globe} label="Account type" value={biz.account_type || "BUSINESS"} />
          <Row icon={Clock} label="Registered on" value={fmtDate(biz.created_at)} />
        </div>
        <div className="card-surface p-5">
          <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-1">Contact</p>
          <Row icon={Phone} label="Phone" value={biz.phone} />
          <Row icon={EnvelopeSimple} label="Email" value={biz.email} />
          <Row icon={Tag} label="Loyalty programme" value={biz.program_description || biz.description} />
        </div>
      </div>
    </div>
  );
}
